/* eslint-disable @typescript-eslint/no-unused-vars */
import GroupService from '../services/group.service';
import EventSequenceService from '../services/event-sequence.service';
import SafetyService from '../services/safety.service';
import ResourceBase from '../@types/resource-base';
import {
    getGroupNameWithAscendants, getGroupTreeItemDescendantIds, getPathToRootAsArrayOfString, groupsTreeItems, makeGroup
} from './group-utils';
import {BreadCrumbsProp} from '../@types/bread-crumb';
import {ResourceType} from '../@types/resource-type';
import Group from '../@types/group';
import usersService from '../services/users.service';
import serviceUserAccountService from '../services/service-user-account.service';
import {userTreeItems} from './user-utils';
import {User} from '../context/userContext';
import EventSequence from '../@types/event-sequence';
import SafetyModel from '../@types/safety-model';
import {
    getEventSequencePathToRootAsArrayOfString,
    getSafetyModelPathToRootAsArrayOfString,
    makeSafetyModel,
    safetyModelsTreeItems
} from './safety-model-utils';
import {WorkspaceResource} from '../@types/workspace-types';
import EventSequenceSummary from '../@types/event-sequence-summary';
import {singularizeCamelCase} from './utils';
import OccurrenceReportDescriptorService from '../services/occurrence-report-descriptor.service';
import OccurrenceService from '../services/occurrence.service';
import NewOccurrenceService from '../services/new-occurrence.service';
import SpringDataPage, {BasicPage, emptyPage, PageInfo} from '../@types/spring-data-page';
import {getOccurrencePathToRootAsArrayOfString, makeOccurrence, occurrenceTreeItems} from './occurrence-utils';
import {getNewOccurrencePathToRootAsArrayOfString, newOccurrenceTreeItems} from './new-occurrence-utils';
import Occurrence from '../@types/occurrence';
import {getPsfTemplatePathToRootAsArrayOfString, makePsfTemplate, psfTemplateTreeItems} from './psf-template-utils';
import PsfTemplateService from '../services/psf-template.service';
import PsfTemplate from '../@types/psfTemplate';
import {serviceUserAccountTreeItems} from './service-user-account-utils';
import NewOccurrenceReport from '../@types/new-occurrence-report';
import {Moment} from 'moment';
import OccurrenceReportDescriptor from '../@types/occurrence-report-descriptor';
import {
    makeOccurrenceReportDescriptor, occurrenceReportDescriptorTreeItems
} from './occurrence-report-descriptor-utils';
import IRPService from '../services/irp.service';
import FoundryService from '../services/foundry.service';
import ComponentService from '../services/component.service';
import {componentTreeItems, makeComponent} from './component-utils';
import {ComponentState} from '../@types/component';

export function getWorkspaceObjectType(value: number): ResourceType {
    switch (value) {
        case 0:
            return ResourceType.GROUPS;
        case 1:
            return ResourceType.SAFETY_MODELS;
        case 2:
            return ResourceType.OCCURRENCE_REPORT_DESCRIPTORS;
        case 3:
            return ResourceType.NEW_OCCURRENCE_REPORTS;
        case 4:
            return ResourceType.OCCURRENCE_REPORTS;
        case 5:
            return ResourceType.PERFORMANCE_SHAPING_FACTORS;
        case 6:
            return ResourceType.TRAFFIC_MODELS;
        case 7:
            return ResourceType.OPERATIONAL_CHANGES;
        case 8:
            return ResourceType.RISK_FORECASTS;
        case 9:
            return ResourceType.WORKSHOPS;
        case 10:
            return ResourceType.USERS;
        case 11:
            return ResourceType.SERVICE_USER_ACCOUNTS;
        case 12:
            return ResourceType.INTEGRATED_RISK_PICTURES;
        case 13:
            return ResourceType.FOUNDRY_EVENTS;
        case 14:
            return ResourceType.COMPONENT;
        case 15:
            return ResourceType.COMPONENT_TREE;
        case 16:
            return ResourceType.SAFETY_RECOMMENDATIONS;
        case 17:
            return ResourceType.COMPONENT_MAPPING;
        case 18:
            return ResourceType.COMPONENT_CATALOGUE;
        default:
            return ResourceType.GROUPS;
    }
}

export function getWorkspaceObjectTypeAsNumber(value: ResourceType): number {
    switch (value) {
        case ResourceType.GROUPS:
            return 0;
        case ResourceType.SAFETY_MODELS:
            return 1;
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return 2;
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return 3;
        case ResourceType.OCCURRENCE_REPORTS:
            return 4;
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return 5;
        case ResourceType.TRAFFIC_MODELS:
            return 6;
        case ResourceType.OPERATIONAL_CHANGES:
            return 7;
        case ResourceType.RISK_FORECASTS:
            return 8;
        case ResourceType.WORKSHOPS:
            return 9;
        case ResourceType.USERS:
            return 10;
        case ResourceType.SERVICE_USER_ACCOUNTS:
            return 11;
        case ResourceType.INTEGRATED_RISK_PICTURES:
            return 12;
        case ResourceType.FOUNDRY_EVENTS:
            return 13;
        case ResourceType.COMPONENT:
            return 14;
        case ResourceType.COMPONENT_TREE:
            return 15;
        case ResourceType.COMPONENT_MAPPING:
            return 17;
        case ResourceType.SAFETY_RECOMMENDATIONS:
            return 16;
        case ResourceType.COMPONENT_CATALOGUE:
            return 18;
        default:
            return 0;
    }
}

export function getWorkspaceObjectLabel(value: number): string {
    switch (value) {
        case 0:
            return 'Working Areas';
        case 1:
            return 'Safety Models';
        case 2:
            return 'Occurrence Report Descriptors';
        case 3:
            return 'New Occurrence Reports';
        case 4:
            return 'Occurrence Reports';
        case 5:
            return 'Performance Shaping Factors';
        case 6:
            return 'Traffic Models';
        case 7:
            return 'Operational Changes';
        case 8:
            return 'Risk Forecasts';
        case 9:
            return 'Workshops';
        case 10:
            return 'Admin';
        case 11:
            return 'Service User Accounts';
        case 12:
            return 'Integrated Risk Pictures';
        case 13:
            return 'Foundry Events';
        case 14:
        case 15:
            return 'Components';
        default:
            return 'Working Areas';
    }
}

export function getChildrenIdOfSelectedWorkspaceObject(type: ResourceType, object: any): number[] {
    switch (type) {
        case ResourceType.GROUPS:
            return (object as Group).getChildren();
        default:
            return [];
    }
}


// @ts-ignore
export function fetchPagedWorkspaceObjects(type: ResourceType, page: BasicPage, groupId?: number | null, searchTerm?: string, groups?: Group[], startSearchDate?: Moment | null, endSearchDate?: Moment | null, selectedDescriptorType?: number): Promise<any[] | SpringDataPage<any>> | null {
    switch (type) {
        case ResourceType.GROUPS:
            return GroupService.getGroups();
        case ResourceType.SAFETY_MODELS:
            return SafetyService.fetchPagedSafetyModels(page, groupId, searchTerm);
        case ResourceType.TRAFFIC_MODELS:
            return null;
        case ResourceType.WORKSHOPS:
            return null;
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return OccurrenceReportDescriptorService.fetchDescriptors(page, groupId, searchTerm, selectedDescriptorType).then(
                descriptors => {
                    let descs = [] as OccurrenceReportDescriptor[];
                    if (groups) {
                        descriptors.content.forEach((desc) => {
                            const group = groups.find((grp) => grp.id === desc.group.id);
                            descs.push({ ...desc, group: group ? group : new Group() });
                        });
                        descriptors.content = descs;

                        return Promise.resolve(descriptors);
                    } else {
                        return GroupService.getGroups().then((groups) => {
                            descriptors.content.forEach((desc) => {
                                const group = groups.find((grp) => grp.id === desc.group.id);
                                descs.push({ ...desc, group: group ? group : new Group() });
                            });
                            descriptors.content = descs;
                            return descriptors; // Ensure descriptors is returned here
                        });
                    }
                }
            );
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return NewOccurrenceService.fetchOccurrences(page, groupId, searchTerm, startSearchDate, endSearchDate).then(
                occurrences => {
                    let occurrs = [] as NewOccurrenceReport[];

                    if (groups) {
                        occurrences.content.forEach((occu) => {
                            const group = groups.find((grp) => grp.id === occu.group.id);
                            if (group) {
                                occurrs.push({...occu, group: group});
                            }
                        });
                        occurrences.content = occurrs;
                        return Promise.resolve(occurrences);
                    } else {
                        return GroupService.getGroups().then((groups) => {
                            occurrences.content.forEach((occu) => {
                                const group = groups.find((grp) => grp.id === occu.group.id);
                                if (group) {
                                    occurrs.push({ ...occu, group: group });
                                }
                            });
                            occurrences.content = occurrs;
                            return occurrences; // Ensure occurrences is returned here
                        });
                    }
                }
            );
        case ResourceType.OCCURRENCE_REPORTS:
            return OccurrenceService.fetchOccurrences(page, groupId, searchTerm).then(
                occurrences => {
                    let occurrs = [] as Occurrence[];

                    if (groups) {
                        occurrences.content.forEach((occu) => {
                            const group = groups.find((grp) => grp.id === occu.group.id);
                            if (group) {
                                occurrs.push({...occu, group: group});
                            }
                        });
                        occurrences.content = occurrs;
                        return Promise.resolve(occurrences);
                    } else {
                        return GroupService.getGroups().then((groups) => {
                            occurrences.content.forEach((occu) => {
                                const group = groups.find((grp) => grp.id === occu.group.id);
                                if (group) {
                                    occurrs.push({ ...occu, group: group });
                                }
                            });
                            occurrences.content = occurrs;
                            return occurrences; // Ensure occurrences is returned here
                        });
                    }
                }
            );
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return PsfTemplateService.fetchPsfTemplates(page, groupId, searchTerm);
        case ResourceType.COMPONENT:
            return ComponentService.fetchPagedComponents(page, {
                searchTerm,
                groupId,
                includeSubGroups: true,
            }).then((componentsPage) => {
                const resolve = (grps: Group[]): SpringDataPage<any> => ({
                    ...componentsPage,
                    content: componentsPage.content.map((component) => ({
                        ...component,
                        group: grps.find((grp) => grp.id === component.groupId),
                    })),
                });
                return groups ? Promise.resolve(resolve(groups)) : GroupService.getGroups().then(resolve);
            });
        case ResourceType.OPERATIONAL_CHANGES:
            return null;
        case ResourceType.RISK_FORECASTS:
            return null;
        case ResourceType.USERS:
            return usersService.getPagedUsers({ size: page.size, offset: page.offset, sort: 'lastName', direction: page.direction }, null, searchTerm);
        case ResourceType.SERVICE_USER_ACCOUNTS:
            return serviceUserAccountService.getPagedServiceUsers({ size: page.size, offset: page.offset, sort: 'username', direction: page.direction }, null, searchTerm);

        default:
            return null;
    }
}

export function fetchAllResources(type: ResourceType): Promise<any[]>|null {
    switch (type) {
        case ResourceType.GROUPS:
            return GroupService.getGroups();
        case ResourceType.SAFETY_MODELS:
            return SafetyService.getSafetyModels();
        case ResourceType.TRAFFIC_MODELS:
            return null;
        case ResourceType.WORKSHOPS:
            return null;
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return null;
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return null;
        case ResourceType.OCCURRENCE_REPORTS:
            return null;
        case ResourceType.OPERATIONAL_CHANGES:
            return null;
        case ResourceType.RISK_FORECASTS:
            return null;
        case ResourceType.USERS:
            return usersService.getAllUsers();
        default:
            return null;
    }
}

export function getResourcesAsPage(resources: any[]|undefined, page: BasicPage, groupId: number|undefined, searchTerm: string|undefined): SpringDataPage<any> {
    if (!resources || resources.length === 0) return emptyPage;
    let filteredResources = resources;
    if (groupId) {
        filteredResources = resources.filter((resource) => resource.id === groupId);
    }
    if (searchTerm && searchTerm.trim() !== '') {
        filteredResources = filteredResources.filter((resource) => resource.name.toLowerCase().includes(searchTerm.trim().toLowerCase()));
    }
    filteredResources = topLevelResources(filteredResources).sort((a, b) => a.compareTo(b));
    let pagedResources = filteredResources;
    if (filteredResources.length > page.size) {
        const start = page.size * page.offset;
        pagedResources = filteredResources.slice(start, start + page.size);
    }
    const pageInfo: PageInfo = {
        number: page.offset,
        size: pagedResources.length,
        totalElements: filteredResources.length,
        totalPages: Math.ceil(filteredResources.length / page.size)
    }
    return {
        content: addChildren(pagedResources, resources),
        page: pageInfo
    };
}

function addChildren(resultedResources: any[], resources: any[]): any[] {
    let resourcesWithChildren = resultedResources;
    resourcesWithChildren.forEach((resource) => {
        if (resource.children && resource.children.length > 0) {
            resourcesWithChildren = resourcesWithChildren.concat(addChildren(resources.filter((res) => resource.children.includes(res.id)), resources));
        }
    })
    return resourcesWithChildren;
}

function topLevelResources(resources: any[]): any[] {
    return resources.filter((resource) =>
        !resource.parent || !resources.find((parent) => parent.id === resource.parent)
    );
}

export function duplicateResourceById(type: ResourceType, sourceResourceId: number | string, targetId: number | string | undefined): Promise<any>|null {
    switch (type) {
        case ResourceType.GROUPS:
            return GroupService.duplicateGroup(sourceResourceId, targetId);
        case ResourceType.SAFETY_MODELS:
            return SafetyService.duplicateSafetyModel(sourceResourceId, targetId);
        case ResourceType.EVENT_SEQUENCES:
        case ResourceType.EVENT_SEQUENCE_TREES:
            return EventSequenceService.duplicateEventSequence(sourceResourceId as number, targetId as number);
        case ResourceType.OCCURRENCE_REPORTS:
            return OccurrenceService.duplicateOccurrence(sourceResourceId, targetId);
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return PsfTemplateService.duplicatePsfTemplate(sourceResourceId, targetId);
        default:
            return null;
    }
}

export function deleteResourceById(type: ResourceType, id: number): Promise<any>|null {
    switch (type) {
        case ResourceType.GROUPS:
            return GroupService.deleteGroupById(id);
        case ResourceType.SAFETY_MODELS:
            return SafetyService.deleteSafetyModelById(id);
        case ResourceType.EVENT_SEQUENCES:
        case ResourceType.EVENT_SEQUENCE_TREES:
            return EventSequenceService.deleteEventSequence(id);
        case ResourceType.OCCURRENCE_REPORTS:
            return OccurrenceService.deleteOccurrenceById(id);
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return PsfTemplateService.deletePsfTemplateById(id);
        default:
            return null;
    }
}

export function createWorkspaceObject(resource: ResourceBase): any {
    switch (resource.type) {
        case ResourceType.GROUPS:
            return (new Group()).setParent(resource.parentId);
        case ResourceType.SAFETY_MODELS:
            return makeSafetyModel({group: { id: resource.parentId }} as SafetyModel);
        case ResourceType.EVENT_SEQUENCES:
            return {} as EventSequenceSummary;
        case ResourceType.EVENT_SEQUENCE_TREES:
            return {} as EventSequence;
        case ResourceType.TRAFFIC_MODELS:
            return { name: 'New Traffic Model', group: { id: resource.parentId } };
        case ResourceType.WORKSHOPS:
            return { name: 'New Workshop', group: { id: resource.parentId } };
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return makeOccurrenceReportDescriptor({group: { id: resource.parentId }} as OccurrenceReportDescriptor);
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return null;
        case ResourceType.OCCURRENCE_REPORTS:
            return makeOccurrence({group: { id: resource.parentId }} as Occurrence);
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return makePsfTemplate({group: { id: resource.parentId }} as PsfTemplate);
        case ResourceType.OPERATIONAL_CHANGES:
            return { name: 'New Operational Change', group: { id: resource.parentId } };
        case ResourceType.RISK_FORECASTS:
            return { name: 'New Risk Forecast', group: { id: resource.parentId } };
        case ResourceType.INTEGRATED_RISK_PICTURES:
            return { name: '', group: { id: resource.parentId } };
        case ResourceType.COMPONENT:
            return makeComponent({
                uniqueId: '',
                name: '',
                description: '',
                state: ComponentState.DRAFT,
                group: resource.parentId ? { id: resource.parentId } : undefined,
            });
        case ResourceType.USERS:
            return {} as User;
        case ResourceType.SERVICE_USER_ACCOUNTS:
            return {
                id: undefined,
                username: '',
                sourceName: '',
                isActive: true,
                superUser: false,
                serviceUser: false
            };
        default:
            return null;
    }
}

export function fetchWorkspaceObject(resource: ResourceBase, groups?: Group[]): Promise<any>|null {
    switch (resource.type) {
        case ResourceType.GROUPS:
            return resource.id ? GroupService.getGroupById(resource.id) : null;
        case ResourceType.SAFETY_MODELS:
            return resource.id ? SafetyService.getSafetyModelById(resource.id) : null;
        case ResourceType.EVENT_SEQUENCES:
            return resource.id ? EventSequenceService.getEventSequenceSummaryById(resource.id) : null;
        case ResourceType.EVENT_SEQUENCE_TREES:
            return resource.id ? EventSequenceService.getEventSequenceById(resource.id) : null;
        case ResourceType.TRAFFIC_MODELS:
            return null;
        case ResourceType.WORKSHOPS:
            return null;
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return resource.id ? OccurrenceReportDescriptorService.fetchDescriptorById(resource.id).then(
                desc => {
                    const group = groups?.find((grp) => grp.id === desc.group.id);
                    if (group) {
                        desc.group = group;
                    }
                    return Promise.resolve(desc);
                }
            ) : null;
        
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return resource.id ? NewOccurrenceService.fetchOccurrenceById(resource.id).then(
                occu => {
                    const group = groups?.find((grp) => grp.id === occu.group.id);
                    if (group) {
                        occu.group = group;
                    }
                    return Promise.resolve(occu);
                }
            ) : null;
        case ResourceType.OCCURRENCE_REPORTS: {
            return resource.id ? OccurrenceService.fetchOccurrenceById(resource.id).then(
                occu => {
                    const group = groups?.find((grp) => grp.id === occu.group.id);
                    if (group) {
                        occu.group = group;
                    }
                    return Promise.resolve(occu);
                }
            ) : null;
        }
        case ResourceType.FOUNDRY_EVENTS:
            return resource.uniqueId ? FoundryService.fetchEventByUniqueId(resource.uniqueId).then(
                event => {
                    return Promise.resolve(event);
                }
            ) : null;
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return resource.id ? PsfTemplateService.fetchPsfTemplateById(resource.id) : null;
        case ResourceType.OPERATIONAL_CHANGES:
            return null;
        case ResourceType.RISK_FORECASTS:
            return null;
        case ResourceType.USERS:
            return resource.id ? usersService.getUserById(resource.id) : null;
        case ResourceType.SERVICE_USER_ACCOUNTS:
            return resource.id ? serviceUserAccountService.getServiceUserById(resource.id) : null;
        case ResourceType.INTEGRATED_RISK_PICTURES:
            return resource.id ? IRPService.getIntegratedRiskPictureById(resource.id) : null;
        case ResourceType.COMPONENT:
        case ResourceType.COMPONENT_TREE:
        case ResourceType.COMPONENT_MAPPING:
        case ResourceType.COMPONENT_HISTORY:
            return resource.id ? ComponentService.getComponent(resource.id) : null;
        default:
            return null;
    }
}

export function getTreeItemDescendantIds(object: any, type: ResourceType, resources: any[]): number[] {
    switch (type) {
        case ResourceType.GROUPS:
            return getGroupTreeItemDescendantIds(object as Group, resources as Group[]);
        default:
            return [];
    }
}

export function generateTreeItems(objects: any[], type: ResourceType): any[] {
    switch (type) {
        case ResourceType.GROUPS:
            return groupsTreeItems(objects);
        case ResourceType.USERS:
            return userTreeItems(objects);
        case ResourceType.SERVICE_USER_ACCOUNTS:
            return serviceUserAccountTreeItems(objects);
        case ResourceType.SAFETY_MODELS:
            return safetyModelsTreeItems(objects);
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return occurrenceReportDescriptorTreeItems(objects);
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return newOccurrenceTreeItems(objects);
        case ResourceType.OCCURRENCE_REPORTS:
            return occurrenceTreeItems(objects);
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return psfTemplateTreeItems(objects);
        case ResourceType.COMPONENT:
            return componentTreeItems(objects);
        default:
            return [];
    }
}

export function getBreadCrumbsProps(object: any, type: ResourceType): BreadCrumbsProp[] {
    switch (type) {
        case ResourceType.GROUPS:
            return getPathToRootAsArrayOfString(object);
        case ResourceType.SAFETY_MODELS:
            return getSafetyModelPathToRootAsArrayOfString(object);
        case ResourceType.EVENT_SEQUENCES:
        case ResourceType.EVENT_SEQUENCE_TREES:
            return getEventSequencePathToRootAsArrayOfString(object);
        case ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS:
            return [...getEventSequencePathToRootAsArrayOfString(object), {type: ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS, id: object.id, label: object.name}];
        case ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS:
            return [...getEventSequencePathToRootAsArrayOfString(object), {type: ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS, id: object.id, label: object.name}];
        case ResourceType.EVENT_SEQUENCE_HISTORY_TREES:
            return [...getEventSequencePathToRootAsArrayOfString(object.eventSequenceDTO), {
                label: 'Version ' + object.id,
                type: ResourceType.EVENT_SEQUENCE_HISTORY_TREES,
                id: object.id
            }];
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return [];
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return getNewOccurrencePathToRootAsArrayOfString(object);
        case ResourceType.OCCURRENCE_REPORTS:
            return getOccurrencePathToRootAsArrayOfString(object);
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return getPsfTemplatePathToRootAsArrayOfString(object);
        default:
            return [];
    }
}

export function getResourceNameWithAscendants(object: any, type: ResourceType): string {
    switch (type) {
        case ResourceType.GROUPS:
            return getGroupNameWithAscendants(object);
        default:
            return object.name ?? '';
    }
}

export function filterOnGroupCanEdit(data: WorkspaceResource): WorkspaceResource {
    let resources = data.resources;
    if (resources) {
        // for some weird reason, data.resources contain objects that are not always instances of Group, they are just plain JS Objects
        // which leads to crashes when checking permissions
        // so we need to ensure we are working with a Group instance
        if (data.objectType === ResourceType.GROUPS) {
            resources = resources.filter((grp: Group) => (grp instanceof Group) ? grp.canEdit() : makeGroup(grp).canEdit());
        }
        return {objectType: ResourceType.GROUPS, resources: resources};
    }
    return data;
}

export function filterOnGroupCanEditResources(data: WorkspaceResource): WorkspaceResource {
    let resources = data.resources;
    if (resources) {
        if (data.objectType === ResourceType.GROUPS) {
            // for some weird reason, data.resources contain objects that are not always instances of Group, they are just plain JS Objects
            // which leads to crashes when checking permissions
            // so we need to ensure we are working with a Group instance
            resources = resources.filter((grp: Group) => (grp instanceof Group) ? grp.canAddOrEditResources() : makeGroup(grp).canAddOrEditResources());
        }
        else if (data.objectType === ResourceType.SAFETY_MODELS) {
            resources = resources.filter((sm: SafetyModel) => sm.group.canAddOrEditResources());
        }
        return {objectType: data.objectType, resources: resources};
    }
    return data;
}

export function getSingularResourceLabel(type: ResourceType|undefined): string {
    let resourceType = type;
    if (resourceType === ResourceType.EVENT_SEQUENCE_TREES || resourceType === ResourceType.EVENT_SEQUENCE_HISTORY) {
        resourceType = ResourceType.EVENT_SEQUENCES;
    }
    if (resourceType === ResourceType.EVENT_SEQUENCE_HISTORY_TREES) {
        resourceType = ResourceType.EVENT_SEQUENCE_HISTORY;
    }
    if (resourceType === ResourceType.COMPONENT_TREE || resourceType === ResourceType.COMPONENT_MAPPING
        || resourceType === ResourceType.COMPONENT_HISTORY || resourceType === ResourceType.COMPONENT_HISTORY_TREE) {
        resourceType = ResourceType.COMPONENT;
    }
    return singularizeCamelCase(resourceType);
}

export function getResourceTypeToOpen(type: ResourceType|undefined): ResourceType {
    let resourceType = type ?? ResourceType.UNKNOWN;
    if (type === ResourceType.EVENT_SEQUENCES || type === ResourceType.EVENT_SEQUENCE_HISTORY || type === ResourceType.EVENT_SEQUENCE_TREES || type === ResourceType.EVENT_SEQUENCE_HISTORY_TREES) {
        resourceType = ResourceType.SAFETY_MODELS;
    }
    return resourceType;
}

export function getSubResourceOnDelete(type: ResourceType): string {
    switch (type) {
        case ResourceType.GROUPS:
            return 'sub groups and resources';
        case ResourceType.SAFETY_MODELS:
            return 'event sequences contained in this safety model';
        case ResourceType.EVENT_SEQUENCES:
        case ResourceType.EVENT_SEQUENCE_TREES:
            return 'occurrence report linked to elements within it';
        case ResourceType.OCCURRENCE_REPORTS:
            return 'sub occurrences and resources';
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return 'performance shaping factor presences associated with it';
        case ResourceType.INTEGRATED_RISK_PICTURES:
            return 'analysis results and mappings';
        case ResourceType.COMPONENT:
            return 'fault tree contained in this component';
        default:
            return '';
    }
}
